import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import { baseUrlAdmin } from '../store/apiConstants';
import Service from '../services';

const TransactionsList = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(20);
    const [filters, setFilters] = useState({
        status: null,
        startDate: null,
        endDate: null,
        orderId: '',
        atomOrderId: '',
        studentName: ''
    });
    const [stats, setStats] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const toast = React.useRef(null);
    const service = new Service();

    // Use useRef to store the debounce timer
    const debounceTimer = useRef(null);

    const statusOptions = [
        { label: 'All', value: null },
        { label: 'Success', value: 'SUCCESS' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Pending', value: 'PENDING' }
    ];

    const loadTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: Math.floor(first / rows) + 1,
                limit: rows
            };

            // Retrieve academic year from localStorage
            const academicYear = localStorage.getItem('userAcademicYear');
            if (academicYear && academicYear.trim() !== '') {
                params.academicYear = academicYear.trim();
            }

            // Add filters to params - ensure we send actual values, not empty strings
            Object.keys(filters).forEach(key => {
                const value = filters[key];
                if (value !== null && value !== undefined && value !== '') {
                    if (key === 'startDate' || key === 'endDate') {
                        params[key] = moment(value).format('YYYY-MM-DD');
                    } else {
                        params[key] = value;
                    }
                }
            });

            // Build URL with query parameters
            const queryString = new URLSearchParams(params).toString();
            const url = `${baseUrlAdmin}/transactions-list${queryString ? '?' + queryString : ''}`;

            const response = await service.get(url, {}, true);

            if (response?.status && response?.res?.success) {
                const transactionsData = response.res.data.transactions || [];
                setTransactions(transactionsData);
                setTotalRecords(response.res.data.pagination.totalRecords || 0);
            } else {
                throw new Error(response?.res?.message || 'Failed to load transactions');
            }
        } catch (error) {
            setTransactions([]);
            setTotalRecords(0);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load transactions'
            });
        } finally {
            setLoading(false);
        }
    }, [first, rows, filters, service]);

    const loadStats = useCallback(async () => {
        try {
            const params = {};
            if (filters.startDate) {
                params.startDate = moment(filters.startDate).format('YYYY-MM-DD');
            }
            if (filters.endDate) {
                params.endDate = moment(filters.endDate).format('YYYY-MM-DD');
            }

            // Retrieve academic year from localStorage
            const academicYear = localStorage.getItem('userAcademicYear');
            if (academicYear && academicYear.trim() !== '') {
                params.academicYear = academicYear.trim();
            }

            // Build URL with query parameters for stats
            const queryString = new URLSearchParams(params).toString();
            const url = `${baseUrlAdmin}/transactions-list/stats${queryString ? '?' + queryString : ''}`;

            const response = await service.get(url, {}, true);

            if (response?.status && response?.res?.success) {
                setStats(response.res.data);
            }
        } catch (error) {
            // Error handled silently for stats
        }
    }, [filters.startDate, filters.endDate, service]);

    // Debounced load function using useRef for timer
    const loadTransactionsDebounced = useCallback(() => {
        // Clear existing timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set new timer
        debounceTimer.current = setTimeout(() => {
            loadTransactions();
        }, 500);
    }, [loadTransactions]);

    const loadStatsDebounced = useCallback(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            loadStats();
        }, 500);  // 500ms delay
    }, [loadStats]);

    // Effect for pagination changes (immediate load)
    useEffect(() => {
        loadTransactions();
    }, [first, rows]);

    // Effect for filter changes (debounced load)
    useEffect(() => {
        loadTransactionsDebounced();

        // Cleanup function
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [filters]);

    // Load stats when date filters change
    useEffect(() => {
        loadStatsDebounced();
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [filters.startDate, filters.endDate]);

    const onPage = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const onFilterChange = (field, value) => {
        // Reset to first page when filtering
        setFirst(0);

        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFirst(0);
        setFilters({
            status: null,
            startDate: null,
            endDate: null,
            orderId: '',
            atomOrderId: '',
            studentName: ''
        });
    };

    const refreshTransaction = async (transactionId) => {
        setRefreshing(true);
        try {
            const url = `${baseUrlAdmin}/transactions-list/${transactionId}/refresh`;
            const response = await service.post(url, {}, true);

            if (response?.status && response?.res?.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Transaction status refreshed successfully'
                });
                loadTransactions();
            } else {
                throw new Error(response?.res?.message || 'Failed to refresh transaction');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to refresh transaction status'
            });
        } finally {
            setRefreshing(false);
        }
    };

    const bulkRefresh = async () => {
        if (selectedTransactions.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please select transactions to refresh'
            });
            return;
        }

        setRefreshing(true);
        try {
            const transactionIds = selectedTransactions.map(t => t.id);
            const url = `${baseUrlAdmin}/transactions-list/bulk-refresh`;
            const response = await service.post(url, { transactionIds }, true);

            if (response?.status && response?.res?.success) {
                const { successful, failed } = response.res.data.summary;
                toast.current?.show({
                    severity: 'success',
                    summary: 'Bulk Refresh Completed',
                    detail: `${successful} successful, ${failed} failed`
                });
                setSelectedTransactions([]);
                loadTransactions();
            } else {
                throw new Error(response?.res?.message || 'Bulk refresh failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Bulk refresh failed'
            });
        } finally {
            setRefreshing(false);
        }
    };

    const showTransactionDetails = async (transaction) => {
        try {
            const url = `${baseUrlAdmin}/transactions-list/${transaction.id}`;
            const response = await service.get(url, {}, true);

            if (response?.status && response?.res?.success) {
                setSelectedTransaction(response.res.data);
                setDetailsVisible(true);
            } else {
                throw new Error(response?.res?.message || 'Failed to load transaction details');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load transaction details'
            });
        }
    };

    const exportTransactions = () => {
        try {
            const params = new URLSearchParams();

            // Retrieve academic year from localStorage
            const academicYear = localStorage.getItem('userAcademicYear');
            if (academicYear && academicYear.trim() !== '') {
                params.append('academicYear', academicYear.trim());
            }

            Object.keys(filters).forEach(key => {
                if (filters[key] !== null && filters[key] !== '') {
                    if (key === 'startDate' || key === 'endDate') {
                        params.append(key, moment(filters[key]).format('YYYY-MM-DD'));
                    } else {
                        params.append(key, filters[key]);
                    }
                }
            });

            const exportUrl = `${baseUrlAdmin}/transactions-list/export?${params.toString()}`;
            const link = document.createElement('a');
            link.href = exportUrl;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.current?.show({
                severity: 'success',
                summary: 'Export Started',
                detail: 'Transaction export has been initiated'
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Export Failed',
                detail: 'Failed to export transactions'
            });
        }
    };

    // Column templates
    const statusBodyTemplate = (rowData) => {
        if (!rowData || !rowData.status) return null;
        const severity = rowData.status === 'SUCCESS' ? 'success' : 'danger';
        return <Tag value={rowData.status} severity={severity} />;
    };

    const amountBodyTemplate = (rowData) => {
        if (!rowData) return '₹0';
        const amount = rowData.amount || 0;
        return `₹${amount.toLocaleString()}`;
    };

    const dateBodyTemplate = (rowData) => {
        if (!rowData || !rowData.createdAt) return 'N/A';
        return moment(rowData.createdAt).format('DD/MM/YYYY HH:mm');
    };

    const studentNameBodyTemplate = (rowData) => {
        if (!rowData) return 'N/A';
        const studentName = rowData.studentName || 'N/A';
        return studentName;
    };

    const actionsBodyTemplate = (rowData) => {
        if (!rowData) return null;

        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => showTransactionDetails(rowData)}
                    tooltip="View Details"
                />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => refreshTransaction(rowData.id)}
                    disabled={refreshing}
                    tooltip="Refresh Status"
                />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row gap-3">
            <div className="flex-1">
                <h2 className="m-0">Payment Transactions</h2>
            </div>
            <div className="flex gap-2">
                <Button
                    icon="pi pi-download"
                    label="Export CSV"
                    onClick={exportTransactions}
                    className="p-button-sm p-button-outlined"
                />
                <Button
                    icon="pi pi-refresh"
                    label="Bulk Refresh"
                    onClick={bulkRefresh}
                    disabled={refreshing || selectedTransactions.length === 0}
                    className="p-button-sm"
                />
                <Button
                    icon="pi pi-filter-slash"
                    label="Clear Filters"
                    onClick={clearFilters}
                    className="p-button-sm p-button-outlined"
                />
                <Button
                    icon="pi pi-refresh"
                    label="Refresh"
                    onClick={() => {
                        setFirst(0);
                        loadTransactions();
                    }}
                    disabled={loading}
                    className="p-button-sm"
                />
            </div>
        </div>
    );

    return (
        <div className="p-4">
            <Toast ref={toast} />

            {/* Statistics Cards */}
            {stats && (
                <div className="grid mb-4">
                    <div className="col-12 md:col-3">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-500">{stats.totalTransactions || 0}</div>
                                <div className="text-sm text-500">Total Transactions</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-500">{stats.successfulTransactions || 0}</div>
                                <div className="text-sm text-500">Successful</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-500">{stats.failedTransactions || 0}</div>
                                <div className="text-sm text-500">Failed</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-500">{stats.successRate || 0}%</div>
                                <div className="text-sm text-500">Success Rate</div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* Filters */}
            <Card className="mb-4">
                <div className="grid">
                    <div className="col-12 md:col-2">
                        <label htmlFor="status" className="block text-sm font-medium mb-1">Status</label>
                        <Dropdown
                            id="status"
                            value={filters.status}
                            options={statusOptions}
                            onChange={(e) => onFilterChange('status', e.value)}
                            placeholder="All"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label htmlFor="startDate" className="block text-sm font-medium mb-1">Start Date</label>
                        <Calendar
                            id="startDate"
                            value={filters.startDate}
                            onChange={(e) => onFilterChange('startDate', e.value)}
                            dateFormat="dd/mm/yy"
                            placeholder="Start Date"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label htmlFor="endDate" className="block text-sm font-medium mb-1">End Date</label>
                        <Calendar
                            id="endDate"
                            value={filters.endDate}
                            onChange={(e) => onFilterChange('endDate', e.value)}
                            dateFormat="dd/mm/yy"
                            placeholder="End Date"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label htmlFor="orderId" className="block text-sm font-medium mb-1">Order ID</label>
                        <InputText
                            id="orderId"
                            value={filters.orderId}
                            onChange={(e) => onFilterChange('orderId', e.target.value)}
                            placeholder="Order ID"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label htmlFor="atomOrderId" className="block text-sm font-medium mb-1">Atom Order ID</label>
                        <InputText
                            id="atomOrderId"
                            value={filters.atomOrderId}
                            onChange={(e) => onFilterChange('atomOrderId', e.target.value)}
                            placeholder="Atom Order ID"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label htmlFor="studentName" className="block text-sm font-medium mb-1">Student Name</label>
                        <InputText
                            id="studentName"
                            value={filters.studentName}
                            onChange={(e) => onFilterChange('studentName', e.target.value)}
                            placeholder="Student Name"
                            className="w-full"
                        />
                    </div>
                </div>
            </Card>

            {/* Data Table */}
            <DataTable
                value={transactions || []}
                lazy
                paginator
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPage={onPage}
                loading={loading}
                header={header}
                selection={selectedTransactions}
                onSelectionChange={(e) => setSelectedTransactions(e.value || [])}
                dataKey="id"
                className="p-datatable-gridlines"
                emptyMessage="No transactions found"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                rowsPerPageOptions={[10, 20, 50, 100]}
                showGridlines
                stripedRows
            >
                <Column
                    selectionMode="multiple"
                    headerStyle={{ width: '3rem' }}
                    bodyStyle={{ textAlign: 'center' }}
                />
                <Column
                    field="studentName"
                    header="Student Name"
                    body={studentNameBodyTemplate}
                    sortable
                    style={{ minWidth: '150px' }}
                />
                <Column
                    field="orderId"
                    header="Order ID"
                    sortable
                    style={{ minWidth: '200px' }}
                />
                <Column
                    field="atomOrderId"
                    header="Atom Order ID"
                    sortable
                    style={{ minWidth: '150px' }}
                />
                <Column
                    field="amount"
                    header="Amount"
                    body={amountBodyTemplate}
                    sortable
                    style={{ minWidth: '100px' }}
                />
                <Column
                    field="status"
                    header="Status"
                    body={statusBodyTemplate}
                    sortable
                    style={{ minWidth: '100px' }}
                />
                <Column
                    field="gateway"
                    header="Gateway"
                    sortable
                    style={{ minWidth: '100px' }}
                />
                <Column
                    field="paymentMode"
                    header="Payment Mode"
                    sortable
                    style={{ minWidth: '120px' }}
                />
                <Column
                    field="createdAt"
                    header="Created"
                    body={dateBodyTemplate}
                    sortable
                    style={{ minWidth: '150px' }}
                />
                <Column
                    header="Actions"
                    body={actionsBodyTemplate}
                    frozen
                    alignFrozen="right"
                    style={{ minWidth: '120px' }}
                />
            </DataTable>

            {/* Transaction Details Dialog */}
            <Dialog
                visible={detailsVisible}
                style={{ width: '80vw' }}
                header="Transaction Details"
                modal
                onHide={() => setDetailsVisible(false)}
            >
                {selectedTransaction && (
                    <div className="grid">
                        <div className="col-12">
                            <h3>Basic Information</h3>
                            <div className="grid">
                                <div className="col-6">
                                    <strong>Transaction ID:</strong> {selectedTransaction._id || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Student Name:</strong> {
                                        selectedTransaction.studentName ||
                                        selectedTransaction.responseData?.customer_details?.customer_name ||
                                        'N/A'
                                    }
                                </div>
                                <div className="col-6">
                                    <strong>Student ID:</strong> {selectedTransaction.responseData?.studentId || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Order ID:</strong> {selectedTransaction.responseData?.order_id || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Atom Order ID:</strong> {selectedTransaction.responseData?.atom_order_id || 'N/A'}
                                </div>
                                <div className="col-6">
                                    <strong>Amount:</strong> ₹{
                                        selectedTransaction.amount ||
                                        selectedTransaction.responseData?.order_amount ||
                                        0
                                    }
                                </div>
                                <div className="col-6">
                                    <strong>Status:</strong>
                                    <Tag
                                        value={selectedTransaction.feeCollected ? 'SUCCESS' : 'FAILED'}
                                        severity={selectedTransaction.feeCollected ? 'success' : 'danger'}
                                        className="ml-2"
                                    />
                                </div>
                                <div className="col-6">
                                    <strong>Created:</strong> {
                                        selectedTransaction.createdAt ?
                                            moment(selectedTransaction.createdAt).format('DD/MM/YYYY HH:mm:ss') :
                                            'N/A'
                                    }
                                </div>
                                <div className="col-6">
                                    <strong>Updated:</strong> {
                                        selectedTransaction.updatedAt ?
                                            moment(selectedTransaction.updatedAt).format('DD/MM/YYYY HH:mm:ss') :
                                            'N/A'
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="col-12">
                            <h3>Full Transaction Data</h3>
                            <pre className="bg-gray-100 p-3 text-sm overflow-auto max-h-20rem">
                                {JSON.stringify(selectedTransaction, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Loading Overlay */}
            {refreshing && (
                <div className="fixed top-0 left-0 w-full h-full flex align-items-center justify-content-center bg-black-alpha-50 z-5">
                    <div className="bg-white p-4 border-round shadow-2">
                        <div className="flex align-items-center gap-3">
                            <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                            <span>Refreshing transaction status...</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionsList;
